/* eslint-disable @typescript-eslint/naming-convention */

import type { Page, Side, Top } from '@ps/web-martech-cms-types';
import { ContentType } from '@ps/web-martech-cms-types';
import { sportsRoutes } from '@ps/web-sports-config';
import { matchPath } from 'react-router';

import type { PageResponseWithRouteMatch } from '@swf/core/models/cms';

import type { BuildPageResponseArgs } from '../types';
import { buildBlockAndItem, buildMainSection } from '../utils';
import { SportsWidgetType } from '../widgets';

export const buildSportsLayoutPageResponse = ({
  alias,
  seoConfig,
  mainSectionWidgetConfigs
}: BuildPageResponseArgs): PageResponseWithRouteMatch => {
  const { block: sportsQuickLinksWidgetBlock, widget: sportsQuickLinksWidget } = buildBlockAndItem(
    'sports_quick_links',
    {
      title: 'Sports Quick Links Widget',
      _content_type_uid: SportsWidgetType.SportsQuickLinks
    }
  );
  const { block: sportsMenuWidgetBlock, widget: sportsMenuWidget } = buildBlockAndItem(
    'sports_menu',
    {
      title: 'Sports Menu Widget',
      _content_type_uid: SportsWidgetType.SportsMenu
    }
  );
  const { blocks: mainSectionWidgetBlocks, widgets: mainSectionWidgets } = buildMainSection([
    ...mainSectionWidgetConfigs,
    {
      title: 'Sports SEO Widget',
      _content_type_uid: SportsWidgetType.SportsSEO,
      ...seoConfig
    }
  ]);

  return {
    content: {
      // Sections
      'isp-sports-section-seo': {
        _content_type_uid: 'seo',
        title: 'ISP SEO Section',
        uid: 'isp-sports-section-seo',
        meta: {
          robots: {
            noindex: seoConfig.noIndex ? true : false,
            nofollow: seoConfig.noFollow ? true : false
          }
        },
        non_translatable: false
      },
      'isp-sports-section-top': {
        _content_type_uid: 'top',
        title: 'ISP Top Section',
        uid: 'isp-sports-section-top',
        body: [sportsQuickLinksWidgetBlock] as unknown as Top['body']
      },
      'isp-sports-section-navigation': {
        _content_type_uid: 'side',
        title: 'ISP Navigation Section',
        uid: 'isp-sports-section-navigation',
        body: [sportsMenuWidgetBlock] as unknown as Side['body']
      },
      'isp-sports-section-main': {
        _content_type_uid: 'main',
        title: 'ISP Main Section',
        uid: 'isp-sports-section-main',
        body: mainSectionWidgetBlocks
      },
      // Shared Widgets
      [sportsQuickLinksWidget.uid]: sportsQuickLinksWidget,
      [sportsMenuWidget.uid]: sportsMenuWidget,
      // Page Widgets
      ...mainSectionWidgets
    },
    page: {
      uid: 'ISP',
      additional_resources: [],
      alias,
      casino_search: [],
      content: [
        {
          shared: true,
          left: [{ uid: 'isp-sports-section-navigation', _content_type_uid: ContentType.Side }],
          main: [{ uid: 'isp-sports-section-main', _content_type_uid: ContentType.Main }],
          seo: [{ uid: 'isp-sports-section-seo', _content_type_uid: ContentType.SEO }],
          side: [{ uid: 'isp-sports-section-right', _content_type_uid: ContentType.Side }],
          top: [{ uid: 'isp-sports-section-top', _content_type_uid: ContentType.Top }],
          website: [],
          header_link: [],
          suppress_marketing_notifications: false
        }
      ],
      enable_optimizely: false,
      hide_navigation: false,
      layout: {
        layout_type: 'SportsLayout',
        background_fit: null // Not used in Sports Layouts
      } as unknown as Page['layout'],
      template: '1 Column', // Not used in Sports Layouts
      title: 'PokerStars Sports', // Not used in Sports Layouts
      url: sportsRoutes[alias],
      visible_in_sitemap: false, // Not needed in the client
      _content_type_uid: 'page'
    },
    isRouteMatch: (path: string) =>
      matchPath(path, {
        path: sportsRoutes[alias],
        exact: true
      })
  };
};
